/* assets/littleg-family-account.js — FAMILY-IDENTITY-PRIVACY-07
 * Owner: Family Center disclosure routing, local-only Child Profile CRUD, active-profile selection,
 * parent sharing confirmation, same-origin return routing, cross-device handoff, and scoped Family-data deletion.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Address Check Workflow Runtime: assets/littleg-family-address-check.js (V03)
 * - Kid Map Canvas & Spatial HUD: assets/littleg-kid-map.js
 * - Read-only Active State & Badges: assets/littleg-kid-active-state.js (V1.12.0)
 * - Device Profile Identity: assets/littleg-profile.js (V1.1.0)
 * - Family Center HTML Owner: sections/littleg-family-account.liquid (V16)
 * - THIS FILE OWNS EXCLUSIVELY:
 *   1. Local child profile state management & CRUD (6 profiles max, local_private mode).
 *   2. Detail panel routing (child profiles, privacy, cross-device) & WCAG focus restoration.
 *   3. Parent sharing gate authorization & same-origin game return handling.
 *   4. Scoped local storage data purging & cross-device link sharing.
 *
 * NON-OWNERSHIP GUARANTEE:
 * No backend write, no authentication, no precise location, no birthdates, no cloud sync claim, no GFun mutation.
 */
(function (window, document) {
  'use strict';

  var VERSION = '2026-09-21-family-identity-privacy-07';
  var ROOT_SELECTOR = '[data-littleg-family-account]';
  var PROFILE_KEY = 'littleg:family:child-profiles:v1';
  var MAX_PROFILES = 6;
  var RETURN_DELAY_MS = 500;
  var AGE_LABELS = {
    'under-6': '6 歲以下',
    '6-8': '6–8 歲',
    '9-12': '9–12 歲',
    '13-plus': '13 歲或以上',
  };

  if (window.__LITTLEG_FAMILY_ACCOUNT__ === VERSION) return;
  window.__LITTLEG_FAMILY_ACCOUNT__ = VERSION;

  var lastPanelTrigger = null;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function safeLocal() {
    try {
      return window.localStorage || null;
    } catch (_) {
      return null;
    }
  }

  function uid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return 'child-' + window.crypto.randomUUID();
      }
    } catch (_) {}
    return 'child-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
  }

  function emptyState() {
    return { version: 1, active_profile_id: '', profiles: [] };
  }

  function normalizeProfile(profile) {
    var source = profile && typeof profile === 'object' ? profile : {};
    return {
      id: clean(source.id) || uid(),
      nickname: clean(source.nickname).slice(0, 24),
      age_group: AGE_LABELS[clean(source.age_group)] ? clean(source.age_group) : '',
      interests: clean(source.interests).slice(0, 80),
      created_at: clean(source.created_at) || new Date().toISOString(),
      updated_at: clean(source.updated_at) || new Date().toISOString(),
      storage_mode: 'local_private',
      precise_location_saved: false,
    };
  }

  function readState() {
    var store = safeLocal();
    if (!store) return emptyState();
    try {
      var parsed = JSON.parse(store.getItem(PROFILE_KEY) || 'null');
      if (!parsed || typeof parsed !== 'object') return emptyState();
      var profiles = Array.isArray(parsed.profiles)
        ? parsed.profiles
            .map(normalizeProfile)
            .filter(function (profile) {
              return profile.nickname && profile.age_group;
            })
            .slice(0, MAX_PROFILES)
        : [];
      var active = clean(parsed.active_profile_id);
      if (
        !profiles.some(function (profile) {
          return profile.id === active;
        })
      ) {
        active = profiles[0] ? profiles[0].id : '';
      }
      return { version: 1, active_profile_id: active, profiles: profiles };
    } catch (_) {
      return emptyState();
    }
  }

  function writeState(state) {
    var store = safeLocal();
    if (!store) return false;
    try {
      store.setItem(
        PROFILE_KEY,
        JSON.stringify({
          version: 1,
          active_profile_id: clean(state.active_profile_id),
          profiles: state.profiles.map(normalizeProfile).slice(0, MAX_PROFILES),
          updated_at: new Date().toISOString(),
        })
      );
      return true;
    } catch (_) {
      return false;
    }
  }

  function clearProfileState() {
    var store = safeLocal();
    if (!store) return false;
    try {
      store.removeItem(PROFILE_KEY);
      return true;
    } catch (_) {
      return false;
    }
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
    } catch (_) {}
  }

  function actionButton(label, action, profileId, className) {
    var node = document.createElement('button');
    node.type = 'button';
    node.className = 'lg-family-account__button' + (className ? ' ' + className : '');
    node.setAttribute('data-profile-action', action);
    node.setAttribute('data-profile-id', profileId);
    node.textContent = label;
    return node;
  }

  function renderProfiles(root, state) {
    var list = root.querySelector('[data-child-profile-list]');
    var empty = root.querySelector('[data-child-profile-empty]');
    var count = root.querySelector('[data-profile-count]');
    if (!list) return;

    list.replaceChildren();
    state.profiles.forEach(function (profile) {
      var article = document.createElement('article');
      article.className = 'lg-family-account__profile';
      article.setAttribute('data-profile-row', profile.id);
      article.setAttribute(
        'data-active',
        profile.id === state.active_profile_id ? 'true' : 'false'
      );

      var avatar = document.createElement('span');
      avatar.className = 'lg-family-account__avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = profile.nickname.slice(0, 1).toUpperCase() || 'G';

      var copy = document.createElement('div');
      copy.className = 'lg-family-account__profile-copy';

      var title = document.createElement('strong');
      title.textContent =
        profile.nickname + (profile.id === state.active_profile_id ? '（目前使用）' : '');

      var meta = document.createElement('span');
      meta.textContent =
        (AGE_LABELS[profile.age_group] || profile.age_group) +
        (profile.interests ? ' · ' + profile.interests : '');
      copy.append(title, meta);

      var actions = document.createElement('div');
      actions.className = 'lg-family-account__profile-actions';
      if (profile.id !== state.active_profile_id) {
        actions.appendChild(actionButton('使用', 'activate', profile.id));
      }
      actions.appendChild(actionButton('編輯', 'edit', profile.id));
      actions.appendChild(
        actionButton('移除', 'delete', profile.id, 'lg-family-account__button--danger')
      );
      article.append(avatar, copy, actions);
      list.appendChild(article);
    });

    if (empty) empty.hidden = state.profiles.length > 0;
    if (count) count.textContent = state.profiles.length + ' / ' + MAX_PROFILES;

    var form = root.querySelector('[data-child-profile-form]');
    if (!form) return;
    var editingId = form.elements.profile_id ? clean(form.elements.profile_id.value) : '';
    var disabled = state.profiles.length >= MAX_PROFILES && !editingId;

    ['nickname', 'age_group', 'interests'].forEach(function (name) {
      if (form.elements[name]) form.elements[name].disabled = disabled;
    });
    var submit = form.querySelector('[data-profile-save]');
    if (submit) submit.disabled = disabled;
  }

  function resetForm(form) {
    if (!form) return;
    form.reset();
    if (form.elements.profile_id) form.elements.profile_id.value = '';
    var save = form.querySelector('[data-profile-save]');
    var cancel = form.querySelector('[data-profile-cancel]');
    if (save) save.textContent = '保存孩子檔案';
    if (cancel) cancel.hidden = true;
  }

  function fillForm(form, profile) {
    if (!form || !profile) return;
    if (form.elements.profile_id) form.elements.profile_id.value = profile.id;
    if (form.elements.nickname) form.elements.nickname.value = profile.nickname;
    if (form.elements.age_group) form.elements.age_group.value = profile.age_group;
    if (form.elements.interests) form.elements.interests.value = profile.interests;

    var save = form.querySelector('[data-profile-save]');
    var cancel = form.querySelector('[data-profile-cancel]');
    if (save) save.textContent = '更新孩子檔案';
    if (cancel) cancel.hidden = false;
    if (form.elements.nickname) form.elements.nickname.focus();
  }

  function closeDetailPanels(root, restoreFocus) {
    var grid = root.querySelector('[data-family-detail-grid]');
    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-detail-panel]'))
      .forEach(function (panel) {
        panel.hidden = true;
      });
    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-panel-target]'))
      .forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
    if (grid) grid.hidden = true;
    root.removeAttribute('data-active-family-panel');

    var targetToFocus = restoreFocus || lastPanelTrigger;
    if (targetToFocus && typeof targetToFocus.focus === 'function') {
      targetToFocus.focus();
      lastPanelTrigger = null;
    }
  }

  function openDetailPanel(root, panelId, trigger, options) {
    var grid = root.querySelector('[data-family-detail-grid]');
    var panel = panelId ? root.querySelector('#' + panelId) : null;
    if (!grid || !panel || !panel.matches('[data-family-detail-panel]')) return false;

    lastPanelTrigger = trigger || document.activeElement;
    closeDetailPanels(root, null);

    grid.hidden = false;
    panel.hidden = false;
    root.setAttribute('data-active-family-panel', panelId);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var settings = options && typeof options === 'object' ? options : {};
    var heading = panel.querySelector('h2');
    if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');

    window.requestAnimationFrame(function () {
      if (settings.scroll !== false) {
        try {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (_) {
          panel.scrollIntoView();
        }
      }
      if (settings.focus !== false && heading) heading.focus({ preventScroll: true });
    });
    return true;
  }

  function urlParams() {
    try {
      return new URL(window.location.href).searchParams;
    } catch (_) {
      return null;
    }
  }

  function requestedPanelId() {
    var params = urlParams();
    if (!params) return '';
    var intent = params.get('intent');
    if (intent === 'parent-confirmation' || intent === 'settings') {
      return 'family-privacy';
    }
    return '';
  }

  function requestedReturnUrl() {
    var params = urlParams();
    if (!params || params.get('intent') !== 'parent-confirmation') return '';
    var raw = clean(params.get('return_url'));
    if (!raw || raw.indexOf('//') === 0) return '';
    try {
      var target = new URL(raw, window.location.origin);
      if (target.origin !== window.location.origin) return '';
      if (target.pathname.indexOf('/pages/game') === -1) return '';
      return target.href;
    } catch (_) {
      return '';
    }
  }

  function updateGateUI(root) {
    var gate = window.LittleGParentGate;
    var authorized = !!(
      gate &&
      typeof gate.isAuthorized === 'function' &&
      gate.isAuthorized('external-share')
    );
    var badge = root.querySelector('[data-parent-gate-badge]');
    var status = root.querySelector('[data-parent-gate-status]');
    if (badge) badge.textContent = authorized ? '已確認' : '未確認';
    if (status) {
      status.textContent = authorized
        ? '本分頁已取得外部分享授權；最多維持 10 分鐘。'
        : '外部分享目前保持鎖定。';
    }
    root.setAttribute('data-parent-gate-authorized', authorized ? 'true' : 'false');
  }

  function familyDeviceUrl(root) {
    return clean(root && root.getAttribute('data-family-device-url'));
  }

  function setDeviceStatus(root, message) {
    var status = root.querySelector('[data-family-device-status]');
    if (status) status.textContent = clean(message);
  }

  function fallbackCopy(value) {
    var textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    var copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (_) {}
    textarea.remove();
    return copied;
  }

  function copyDeviceUrl(root) {
    var value = familyDeviceUrl(root);
    if (!value) {
      setDeviceStatus(root, '未能取得家庭中心連結。');
      return Promise.resolve(false);
    }

    var operation =
      window.navigator.clipboard && typeof window.navigator.clipboard.writeText === 'function'
        ? window.navigator.clipboard.writeText(value).then(function () {
            return true;
          })
        : Promise.resolve(fallbackCopy(value));

    return operation
      .then(function (copied) {
        setDeviceStatus(
          root,
          copied ? '已複製家庭中心連結。' : '未能自動複製；請長按上方連結手動複製。'
        );
        return copied;
      })
      .catch(function () {
        var copied = fallbackCopy(value);
        setDeviceStatus(
          root,
          copied ? '已複製家庭中心連結。' : '未能自動複製；請長按上方連結手動複製。'
        );
        return copied;
      });
  }

  function shareDeviceUrl(root) {
    var value = familyDeviceUrl(root);
    if (!value) {
      setDeviceStatus(root, '未能取得家庭中心連結。');
      return;
    }

    if (window.navigator.share && typeof window.navigator.share === 'function') {
      window.navigator
        .share({
          title: 'LittleG 家庭中心',
          text: '在電腦開啟 LittleG 家庭中心。',
          url: value,
        })
        .then(function () {
          setDeviceStatus(root, '已開啟系統分享。');
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') {
            setDeviceStatus(root, '已取消分享。');
            return;
          }
          copyDeviceUrl(root);
        });
      return;
    }

    copyDeviceUrl(root);
  }

  function removeFamilyResults() {
    var familyData = window.LittleGFamilyData;
    var resultStorage = window.LittleGResultStorage;
    if (
      !familyData ||
      typeof familyData.listRecords !== 'function' ||
      !resultStorage ||
      typeof resultStorage.removeResult !== 'function'
    ) {
      return 0;
    }

    var removed = 0;
    familyData.listRecords().forEach(function (record) {
      var id = clean(record && (record.result_id || record.record_id || record.card_id));
      if (id && resultStorage.removeResult(id)) removed += 1;
    });
    return removed;
  }

  function clearNoticeDraft() {
    var familyData = window.LittleGFamilyData;
    if (!familyData || typeof familyData.clearNoticeDraft !== 'function') return false;
    return familyData.clearNoticeDraft('family-center-clear');
  }

  function clearFamilyData(root) {
    var removedRecords = removeFamilyResults();
    var profileCleared = clearProfileState();
    var draftCleared = clearNoticeDraft();

    if (window.LittleGParentGate && typeof window.LittleGParentGate.clear === 'function') {
      window.LittleGParentGate.clear();
    }

    var state = emptyState();
    renderProfiles(root, state);
    resetForm(root.querySelector('[data-child-profile-form]'));
    var status = root.querySelector('[data-family-data-status]');
    if (status) {
      status.textContent =
        '已清除本裝置的孩子檔案、家庭記錄、通告草稿與臨時分享確認；其他產品記錄不受影響。';
    }
    emit('littleg:family-data-cleared', {
      source: 'family-account',
      local_only: true,
      profile_cleared: profileCleared,
      notice_draft_cleared: draftCleared,
      removed_family_records: removedRecords,
      other_product_records_preserved: true,
    });
    return state;
  }

  function bind(root) {
    if (!root || root.getAttribute('data-family-account-bound') === VERSION) return;
    root.setAttribute('data-family-account-bound', VERSION);

    var state = readState();
    var form = root.querySelector('[data-child-profile-form]');
    var parentReturnUrl = requestedReturnUrl();

    renderProfiles(root, state);
    updateGateUI(root);
    closeDetailPanels(root, null);

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var id = clean(form.elements.profile_id ? form.elements.profile_id.value : '');
        var nickname = clean(form.elements.nickname ? form.elements.nickname.value : '');
        var ageGroup = clean(form.elements.age_group ? form.elements.age_group.value : '');
        var interests = clean(form.elements.interests ? form.elements.interests.value : '');

        if (!nickname || !AGE_LABELS[ageGroup]) return;
        if (!id && state.profiles.length >= MAX_PROFILES) return;

        var existing = state.profiles.find(function (profile) {
          return profile.id === id;
        });
        var profile = normalizeProfile({
          id: id || uid(),
          nickname: nickname,
          age_group: ageGroup,
          interests: interests,
          created_at: existing && existing.created_at,
          updated_at: new Date().toISOString(),
        });

        if (existing) {
          state.profiles = state.profiles.map(function (item) {
            return item.id === profile.id ? profile : item;
          });
        } else {
          state.profiles.push(profile);
          if (!state.active_profile_id) state.active_profile_id = profile.id;
        }

        if (writeState(state)) {
          resetForm(form);
          renderProfiles(root, state);
          emit('littleg:child-profile-change', {
            action: existing ? 'updated' : 'created',
            profile: profile,
            active_profile_id: state.active_profile_id,
          });
        }
      });

      var cancel = form.querySelector('[data-profile-cancel]');
      if (cancel) {
        cancel.addEventListener('click', function () {
          resetForm(form);
          renderProfiles(root, state);
        });
      }
    }

    root.addEventListener('click', function (event) {
      var shareTrigger =
        event.target && event.target.closest
          ? event.target.closest('[data-family-device-share]')
          : null;
      if (shareTrigger) {
        shareDeviceUrl(root);
        return;
      }

      var copyTrigger =
        event.target && event.target.closest
          ? event.target.closest('[data-family-device-copy]')
          : null;
      if (copyTrigger) {
        copyDeviceUrl(root);
        return;
      }

      var panelTrigger =
        event.target && event.target.closest
          ? event.target.closest('[data-family-panel-target]')
          : null;
      if (panelTrigger) {
        lastPanelTrigger = panelTrigger;
        openDetailPanel(root, panelTrigger.getAttribute('data-family-panel-target'), panelTrigger);
        return;
      }

      var closePanelBtn =
        event.target && event.target.closest
          ? event.target.closest('[data-family-panel-close]')
          : null;
      if (closePanelBtn) {
        closeDetailPanels(root, lastPanelTrigger);
        return;
      }

      var action =
        event.target && event.target.closest ? event.target.closest('[data-profile-action]') : null;
      if (!action) return;

      var id = clean(action.getAttribute('data-profile-id'));
      var profile = state.profiles.find(function (item) {
        return item.id === id;
      });
      if (!profile) return;
      var type = action.getAttribute('data-profile-action');

      if (type === 'activate') {
        state.active_profile_id = id;
        writeState(state);
        renderProfiles(root, state);
        emit('littleg:child-profile-change', {
          action: 'activated',
          profile: profile,
          active_profile_id: id,
        });
      } else if (type === 'edit') {
        fillForm(form, profile);
      } else if (type === 'delete') {
        if (action.getAttribute('data-delete-confirm') !== 'true') {
          action.setAttribute('data-delete-confirm', 'true');
          action.textContent = '再按一次確認移除';
          return;
        }
        state.profiles = state.profiles.filter(function (item) {
          return item.id !== id;
        });
        if (state.active_profile_id === id) {
          state.active_profile_id = state.profiles[0] ? state.profiles[0].id : '';
        }
        writeState(state);
        resetForm(form);
        renderProfiles(root, state);
        emit('littleg:child-profile-change', {
          action: 'removed',
          profile_id: id,
          active_profile_id: state.active_profile_id,
        });
      }
    });

    // 鍵盤 Escape 事件關閉側邊 Detail Panel
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        var activePanel = root.querySelector('[data-family-detail-panel]:not([hidden])');
        if (activePanel) {
          closeDetailPanels(root, lastPanelTrigger);
        }
      }
    });

    var checkbox = root.querySelector('[data-parent-consent-checkbox]');
    var authorize = root.querySelector('[data-parent-consent-authorize]');
    var clearGate = root.querySelector('[data-parent-consent-clear]');

    if (checkbox && authorize) {
      checkbox.addEventListener('change', function () {
        authorize.disabled = !checkbox.checked;
      });
      authorize.addEventListener('click', function () {
        var gate = window.LittleGParentGate;
        var ok = !!(
          checkbox.checked &&
          gate &&
          typeof gate.authorize === 'function' &&
          gate.authorize({
            confirmed: true,
            scope: 'external-share',
            source: 'family-account-parent-confirmation',
          })
        );
        if (!ok) return;

        checkbox.checked = false;
        authorize.disabled = true;
        updateGateUI(root);

        if (parentReturnUrl) {
          var status = root.querySelector('[data-parent-gate-status]');
          if (status) status.textContent = '家長確認完成，正在返回原本的分享畫面。';
          root.setAttribute('data-parent-confirmation-return', 'pending');
          window.setTimeout(function () {
            window.location.assign(parentReturnUrl);
          }, RETURN_DELAY_MS);
        }
      });
    }

    if (clearGate) {
      clearGate.addEventListener('click', function () {
        if (window.LittleGParentGate && typeof window.LittleGParentGate.clear === 'function') {
          window.LittleGParentGate.clear();
        }
        updateGateUI(root);
      });
    }

    var clearData = root.querySelector('[data-family-data-clear]');
    if (clearData) {
      clearData.addEventListener('click', function () {
        if (clearData.getAttribute('data-clear-confirm') !== 'true') {
          clearData.setAttribute('data-clear-confirm', 'true');
          clearData.textContent = '再按一次，清除本裝置家庭資料';
          return;
        }
        state = clearFamilyData(root);
        clearData.removeAttribute('data-clear-confirm');
        clearData.textContent = '清除本裝置資料';
      });
    }

    var requested = requestedPanelId();
    if (requested) {
      var requestedTrigger = root.querySelector('[data-family-panel-target="' + requested + '"]');
      lastPanelTrigger = requestedTrigger;
      openDetailPanel(root, requested, requestedTrigger, { scroll: true, focus: true });
      root.setAttribute('data-family-entry-intent', 'parent-confirmation');
      root.setAttribute(
        'data-parent-confirmation-return',
        parentReturnUrl ? 'available' : 'unavailable'
      );
    }
  }

  window.LittleGChildProfiles = Object.freeze({
    VERSION: VERSION,
    storageMode: 'local_private',
    list: function () {
      return readState().profiles.slice();
    },
    getActive: function () {
      var state = readState();
      return (
        state.profiles.find(function (profile) {
          return profile.id === state.active_profile_id;
        }) || null
      );
    },
  });

  function boot(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll(ROOT_SELECTOR)).forEach(bind);
  }

  document.addEventListener('littleg:parent-gate-change', function () {
    Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR)).forEach(updateGateUI);
  });

  // Shopify Section Unload 記憶體釋放與監聽解綁
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target ? event.target.querySelector(ROOT_SELECTOR) : null;
    if (!(root instanceof HTMLElement)) return;
    root.removeAttribute('data-family-account-bound');
    lastPanelTrigger = null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})(window, document);
